import { inject, Injectable } from '@angular/core';

import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import {
  LocalStorageService,
  ModalService,
  ShowError,
  ShowInfo
} from '@ui/legacy-lib';
import { storageKeys } from '@ui/tenant-pool/shared/config/util';
import { SelfDisclosureFacade } from '../../core/services/self-disclosure.facade';
import { notificationConfig } from '../../config/notification.config';
import { SelfDisclosureModalComponent } from '../../components/self-disclosure/self-disclosure-modal/self-disclosure-modal.component';
import {
  getLoadModelActionState,
  getLoadResponsesActionState,
  getSelfDisclosureModel,
  getSelfDisclosurePropertySearcher,
  getSelfDisclosureResponses
} from './self-disclosure.selectors';
import {
  LOAD_SELF_DISCLOSURE_ANSWERS,
  LOAD_SELF_DISCLOSURE_DATA,
  LOAD_SELF_DISCLOSURE_MODEL,
  LoadSelfDisclosureAnswers,
  LoadSelfDisclosureAnswersFail,
  LoadSelfDisclosureAnswersSuccess,
  LoadSelfDisclosureData,
  LoadSelfDisclosureModel,
  LoadSelfDisclosureModelFail,
  LoadSelfDisclosureModelSuccess,
  OPEN_SELF_DISCLOSURE_MODAL,
  OpenSelfDisclosuresModal,
  SAVE_SELF_DISCLOSURE_ANSWERS,
  SavePropertySearcher,
  SaveSelfDisclosureAnswers,
  SaveSelfDisclosureAnswersFail,
  SaveSelfDisclosureAnswersSuccess
} from './self-disclosure.actions';

@Injectable()
export class SelfDisclosureEffects {
  private actions$ = inject(Actions);
  loadSelfDisclosureData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadSelfDisclosureData>(LOAD_SELF_DISCLOSURE_DATA),
      mergeMap(({ propertySearcher }) => [
        new SavePropertySearcher(propertySearcher),
        new LoadSelfDisclosureModel(propertySearcher.property.id),
        new LoadSelfDisclosureAnswers(propertySearcher.id)
      ])
    )
  );
  private selfDisclosureFacade = inject(SelfDisclosureFacade);
  loadSelfDisclosureModel$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadSelfDisclosureModel>(LOAD_SELF_DISCLOSURE_MODEL),
      switchMap(({ propertyId }) =>
        this.selfDisclosureFacade.loadSelfDisclosureModel(propertyId).pipe(
          map(
            ({ selfDisclosureModel }) =>
              new LoadSelfDisclosureModelSuccess(selfDisclosureModel)
          ),
          catchError(err => [
            new LoadSelfDisclosureModelFail(err && err.message),
            new ShowError(notificationConfig.selfDisclosure.load.error)
          ])
        )
      )
    )
  );
  getSelfDisclosureAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType<LoadSelfDisclosureAnswers>(LOAD_SELF_DISCLOSURE_ANSWERS),
      switchMap(({ propertySearcherId }) =>
        this.selfDisclosureFacade.getAnswers(propertySearcherId).pipe(
          map(answers => new LoadSelfDisclosureAnswersSuccess(answers)),
          catchError(err => [
            new LoadSelfDisclosureAnswersFail(err && err.message),
            new ShowError(notificationConfig.selfDisclosure.loadAnswer.error)
          ])
        )
      )
    )
  );
  saveSelfDisclosureAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveSelfDisclosureAnswers>(SAVE_SELF_DISCLOSURE_ANSWERS),
      switchMap(({ id, answers }) =>
        this.selfDisclosureFacade.saveAnswers(id, answers).pipe(
          mergeMap(() => [
            new SaveSelfDisclosureAnswersSuccess(answers),
            new ShowInfo(notificationConfig.selfDisclosure.answer.success)
          ]),
          catchError(err => [
            new SaveSelfDisclosureAnswersFail(err && err.message),
            new ShowError(notificationConfig.selfDisclosure.answer.error)
          ])
        )
      )
    )
  );
  private modalService = inject(ModalService);
  private store = inject(Store);
  private localStorageService = inject(LocalStorageService);
  openSelfDisclosureModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<OpenSelfDisclosuresModal>(OPEN_SELF_DISCLOSURE_MODAL),
        tap(() => {
          if (
            this.localStorageService.getItem(
              storageKeys.selfDisclosureDeclareIntentId
            )
          )
            this.localStorageService.removeItem(
              storageKeys.selfDisclosureDeclareIntentId
            );
        }),
        switchMap(action =>
          this.store.select(getLoadResponsesActionState).pipe(
            filter(state => !state.pending && state.done),
            take(1),
            switchMap(() =>
              this.store
                .select(getLoadModelActionState)
                .pipe(
                  filter(state => !state.pending && state.done),
                  take(1)
                )
                .pipe(
                  withLatestFrom(
                    this.store.select(getSelfDisclosureResponses),
                    this.store.select(getSelfDisclosureModel),
                    this.store.select(getSelfDisclosurePropertySearcher)
                  ),
                  filter(
                    ([
                      _,
                      selfDisclosureResponses,
                      selfDisclosureModel,
                      propertyMatch
                    ]) =>
                      !!propertyMatch &&
                      !!selfDisclosureModel &&
                      !!selfDisclosureResponses
                  ),
                  take(1),
                  tap(
                    ([
                      _,
                      selfDisclosureResponses,
                      selfDisclosureModel,
                      propertyMatch
                    ]) => {
                      this.modalService.open(SelfDisclosureModalComponent, {
                        backdrop: 'static',
                        keyboard: false,
                        data: {
                          selfDisclosureModel,
                          selfDisclosureResponses,
                          propertyMatch,
                          hasIntent: action.hasIntent
                        }
                      });
                    }
                  )
                )
            )
          )
        )
      ),
    { dispatch: false }
  );
}
